import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "tca_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const tzOffset = () => -new Date().getTimezoneOffset();

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Auth ----
export const registerUser = (payload) =>
  http.post(`/auth/register`, payload).then((r) => r.data);
export const loginUser = (email, password) =>
  http.post(`/auth/login`, { email, password }).then((r) => r.data);
export const logoutUser = () => http.post(`/auth/logout`).then((r) => r.data);

// ---- Data export (Export v1): downloads a zip of the requesting user's own data ----
export async function exportMyData() {
  const res = await http.get(`/me/export`, { responseType: "blob" });
  let filename = "heldall-export.zip";
  const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"];
  const m = cd && /filename="?([^"]+)"?/.exec(cd);
  if (m && m[1]) filename = m[1];
  return { blob: res.data, filename };
}
export const fetchMe = () => http.get(`/auth/me`).then((r) => r.data);
export const fetchInvite = () => http.post(`/auth/invite`).then((r) => r.data);
export const forgotPassword = (email) =>
  http.post(`/auth/forgot-password`, { email }).then((r) => r.data);
export const resetPassword = (token, new_password) =>
  http.post(`/auth/reset-password`, { token, new_password }).then((r) => r.data);

// ---- Push (Delivery v1) ----
export const getVapidKey = () => http.get(`/push/vapid-public-key`).then((r) => r.data);
export const subscribePush = (subscription, user_agent) =>
  http.post(`/push/subscribe`, { subscription, user_agent }).then((r) => r.data);
export const unsubscribePush = (endpoint) =>
  http.post(`/push/unsubscribe`, { endpoint }).then((r) => r.data);
export const sendTestPush = (endpoint) =>
  http.post(`/push/test`, { endpoint }).then((r) => r.data);
export const setHandoffPush = (enabled) =>
  http.post(`/push/handoff-preference`, { enabled }).then((r) => r.data);
export const setDailyNudge = (enabled) =>
  http.post(`/push/daily-nudge-preference`, { enabled }).then((r) => r.data);
export const setImpliedSuggestions = (enabled) =>
  http.post(`/me/implied-suggestions-preference`, { enabled }).then((r) => r.data);
export const dismissPartnerOnboarding = () =>
  http.post(`/me/partner-onboarding-seen`).then((r) => r.data);
export const removePartner = (partner_id) =>
  http.post(`/household/remove-partner`, partner_id ? { partner_id } : {}).then((r) => r.data);
export const leaveHousehold = () =>
  http.post(`/household/leave`).then((r) => r.data);
export const getInviteInfo = (code) =>
  http.get(`/household/invite-info`, { params: { code } }).then((r) => r.data);
export const joinHousehold = (invite_code) =>
  http.post(`/household/join`, { invite_code }).then((r) => r.data);
export const deleteMyAccount = () =>
  http.post(`/me/delete`).then((r) => r.data);
export const startFresh = () =>
  http.post(`/me/start-fresh`).then((r) => r.data);

// ---- Capture / entries ----
export const captureThought = (raw_text) =>
  http.post(`/capture`, { raw_text, tz_offset: tzOffset() }).then((r) => r.data);

export const analyzeCapture = (raw_text) =>
  http.post(`/capture/analyze`, { raw_text, tz_offset: tzOffset() }).then((r) => r.data);

export const confirmCapture = (drafts) =>
  http.post(`/capture/confirm`, { drafts }).then((r) => r.data);

// ---- Talk it through (PREVIEW, Capture V2): extract multiple items from a messy stream ----
export const talkCapture = (raw_text, source = "typed") =>
  http.post(`/capture/talk`, { raw_text, tz_offset: tzOffset(), source }).then((r) => r.data);

// ---- Image / screenshot capture (PREVIEW): vision -> transcript -> same extraction pipeline ----
export const captureImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("tz_offset", String(tzOffset()));
  return http.post(`/capture/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// ---- Voice capture (PREVIEW): MediaRecorder audio -> whisper-1 -> same extraction pipeline ----
export const captureVoice = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("tz_offset", String(tzOffset()));
  return http.post(`/capture/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const getPrepSuggestions = (payload) =>
  http.post(`/capture/prep-suggestions`, payload).then((r) => r.data);

export const getEntries = (status) =>
  http.get(`/entries`, { params: status ? { status } : {} }).then((r) => r.data);

export const updateEntry = (id, changes) =>
  http.patch(`/entries/${id}`, changes).then((r) => r.data);

export const deleteEntry = (id) =>
  http.delete(`/entries/${id}`).then((r) => r.data);

export const restoreEntry = (id) =>
  http.post(`/entries/${id}/restore`).then((r) => r.data);

export const getDeletedEntries = () =>
  http.get(`/entries/deleted`).then((r) => r.data);

export const purgeEntry = (id) =>
  http.delete(`/entries/${id}/purge`).then((r) => r.data);

export const purgeAllDeleted = () =>
  http.delete(`/entries/deleted/all`).then((r) => r.data);

export const markSeen = (ids) =>
  http.post(`/entries/seen`, { ids }).then((r) => r.data);

export const getActivity = () => http.get(`/activity`).then((r) => r.data);

// ---- Ahead (forward schedule) ----
export const getAheadSummary = (items, scope) =>
  http.post(`/ahead/summary`, { items, scope }).then((r) => r.data);
export const getAheadFocus = (items, scope) =>
  http.post(`/ahead/focus`, { items, scope }).then((r) => r.data);


export const getSettings = () => http.get(`/settings`).then((r) => r.data);

export const saveSettings = (settings) =>
  http.put(`/settings`, settings).then((r) => r.data);

// ---- WhatsApp capture (optional) ----
export const getWhatsappStatus = () => http.get(`/whatsapp/status`).then((r) => r.data);
export const linkWhatsapp = (whatsapp_number) =>
  http.post(`/whatsapp/link`, { whatsapp_number }).then((r) => r.data);
export const unlinkWhatsapp = () => http.post(`/whatsapp/unlink`).then((r) => r.data);

// ---- Notifications (Delivery v1) ----
export const getNotificationSettings = () => http.get(`/notifications/settings`).then((r) => r.data);
export const saveNotificationSettings = (settings) =>
  http.put(`/notifications/settings`, settings).then((r) => r.data);
